import { useContext } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { List, Spacer } from '@zillow/constellation';
import { Button, ButtonGroup, Heading, Modal, Text, TextButton } from '@zillow/constellation-10';

import { PaymentGatewayContext } from '../../PaymentGateway/context';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

const propTypes = {
  onClose: PropTypes.func.isRequired,
  onPublishForFreeClick: PropTypes.func.isRequired,
  onTryAgainClick: PropTypes.func.isRequired,
  error: PropTypes.object,
};

const defaultProps = {
  error: null,
};

const FreemiumErrorModal = ({ onClose, onPublishForFreeClick, onTryAgainClick, error }) => {
  const {
    pgState: { isPremiumMidfunnelPayment },
  } = useContext(PaymentGatewayContext);

  const { selectedProductType } = useSelector((state) => {
    return {
      selectedProductType: state.properties.currentProperty.selectedProductType,
    };
  }, shallowEqual);

  const getErrorMessage = (error) => {
    if (!error) return null;

    // Handle charge error format
    if (error.error?.message) {
      return error.error.message;
    }

    // Handle activation error format
    const { errorDetails, message: errorMessage } = error;
    const { listingActivationFailed, activationError } = errorDetails || {};

    // general message
    let message = errorMessage;

    if (listingActivationFailed) {
      if (activationError?.message) {
        // more specific message
        message = activationError?.message;
      }
    }

    return message;
  };

  let cancelProps = {
    submitText: 'Try again',
    onSubmit: selectedProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? onTryAgainClick : onPublishForFreeClick,
  };
  if (isPremiumMidfunnelPayment) {
    cancelProps.cancelText = 'Cancel and edit listing';
    cancelProps.onCancel = onClose;
  } else if (selectedProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM) {
    cancelProps.cancelText = 'Publish for free';
    cancelProps.onCancel = onPublishForFreeClick;
  }

  return (
    <Modal
      data-test="FreemiumErrorModal"
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      header={<Heading level={1}>Error</Heading>}
      body={
        <>
          <Text asChild>
            <p>
              {getErrorMessage(error) ||
                "An error occurred with your payment method and your premium upgrade for this listing couldn't be processed. Please retry or use another payment method to upgrade to premium. Or, if you prefer, you can publish your listing for free now and upgrade to premium later."}
            </p>
          </Text>
          {error?.data?.listing && Object.entries(error.data.listing).length > 0 && (
            <Spacer paddingTop="sm">
              <Text asChild>
                <p>Details:</p>
              </Text>
              <List>
                {Object.entries(error.data.listing).map(
                  ([key, value]) => typeof value === 'string' && <List.Item key={key}>{value}</List.Item>,
                )}
              </List>
            </Spacer>
          )}
        </>
      }
      footer={
        <ButtonGroup aria-label="modal actions">
          <TextButton onClick={cancelProps.onCancel}>{cancelProps.cancelText}</TextButton>
          <Button emphasis="filled" tone="brand" onClick={cancelProps.onSubmit}>
            {cancelProps.submitText}
          </Button>
        </ButtonGroup>
      }
    ></Modal>
  );
};

FreemiumErrorModal.propTypes = propTypes;
FreemiumErrorModal.defaultProps = defaultProps;

export default FreemiumErrorModal;
